import { useQueryClient } from '@tanstack/react-query';
import { EditOutlined } from '@ant-design/icons';
import { Button, Flex, Input } from 'antd';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { brokerCasesApi } from 'api/brokerCases';
import { CASE_TASKS_QUERY_KEY } from 'queries/brokerCases';
import { TypedFormatMessageFn } from 'types/intl';

const isValidCode = (value: string) => /^[A-z0-9/]{1,9}$/.test(value);

export interface IEvidenceShareCodeInputProps {
  caseId: string;
  individualId: string;
  taskId: string;
  initialCode?: string;
}

export const EvidenceShareCodeInputAction = ({
  caseId,
  individualId,
  taskId,
  initialCode,
}: IEvidenceShareCodeInputProps) => {
  const queryClient = useQueryClient();
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [code, setCode] = useState(initialCode ?? '');
  const [isEditing, setIsEditing] = useState(!initialCode?.length);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isCodeValid = isValidCode(code);

  const handleShareCodeConfirm = async () => {
    if (!isCodeValid) return;
    try {
      setIsSubmitting(true);
      await brokerCasesApi.patchIndividualShareCode(caseId, individualId, { shareCode: code, taskId });
      setIsEditing(false);
      await queryClient.invalidateQueries({ queryKey: [CASE_TASKS_QUERY_KEY, caseId] });
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : formatMessage({ id: 'evidence.shareCode.saveFailed' }), {
        variant: 'error',
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Flex align='center' className='gap-2'>
      <Input
        placeholder={formatMessage({ id: 'evidence.shareCode.placeholder' })}
        value={code}
        allowClear={isEditing}
        onClear={() => {
          setCode(initialCode ?? '');
          setIsEditing(false);
        }}
        disabled={!isEditing || isSubmitting}
        onChange={e =>
          setCode(
            e.target.value
              .replace(/[^\d\w]/g, '')
              .slice(0, 9)
              .toUpperCase(),
          )
        }
        className='w-40 font-normal text-xs leading-5'
      />
      {!isEditing ? (
        <Button
          type='text'
          disabled={isSubmitting}
          className='w-6 h-6 flex shrink-0 items-center justify-center border-0 bg-transparent p-0 shadow-none'
          icon={<EditOutlined className='text-xs leading-none' />}
          onClick={() => setIsEditing(true)}
        />
      ) : (
        <Button
          type='default'
          disabled={!isCodeValid}
          loading={isSubmitting}
          className={`border px-4 py-1.5 font-normal text-xs leading-5 shadow-none ${
            isCodeValid
              ? 'border-none bg-green-500 text-white hover:!bg-green-600 active:!bg-green-700'
              : 'border-slate-300 bg-black/5 text-black/25'
          }`}
          onClick={handleShareCodeConfirm}
        >
          {formatMessage({ id: 'evidence.shareCode.confirm' })}
        </Button>
      )}
    </Flex>
  );
};
